import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import RequestBrief from '../components/common/RequestBrief';

const formatSize = (bytes) => (bytes >= 1024 * 1024 * 1024
  ? `${Math.round((bytes / 1024 / 1024 / 1024) * 10) / 10} GB`
  : `${Math.max(1, Math.round(bytes / 1024 / 1024))} MB`);

const formatWhen = (value) => new Date(value).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
});

// A submitter's private page for one intake-form request, at /u/:token. Shows
// the same brief + files Gem City sees, with a box to comment or send more
// files; each update lands on the card and pings whoever watches the form.
// No login — the token is the credential.
const RequestUpdate = () => {
  const { token } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [honeypot, setHoneypot] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    const res = await axios.get(`/api/forms/request/${token}`);
    setRequest(res.data);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/forms/request/${token}`);
        if (!cancelled) setRequest(res.data);
      } catch (err) {
        if (!cancelled) setNotFound(err.response?.data?.msg || 'This link is no longer active.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Private links stay out of search results.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (request?.title) document.title = `Update: ${request.title}`;
  }, [request]);

  const fileUrl = (id) => `${axios.defaults.baseURL || ''}/api/forms/request/${token}/files/${id}`;
  const zipUrl = `${axios.defaults.baseURL || ''}/api/forms/request/${token}/files.zip`;

  const uploadDirect = async (file, onProgress) => {
    const { data } = await axios.post(`/api/forms/request/${token}/upload-url`, {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size
    });
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', data.uploadUrl);
      xhr.setRequestHeader('Content-Type', data.contentType);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) onProgress(Math.round((evt.loaded / evt.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
      xhr.send(file);
    });
    return { key: data.key, name: file.name };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!message.trim() && !files.length) return setError('Add a comment or at least one file.');
    const oversize = files.find(f => f.size > request.maxFileBytes);
    if (oversize) return setError(`"${oversize.name}" is larger than ${formatSize(request.maxFileBytes)}.`);

    setSending(true);
    try {
      if (request.directUpload) {
        const uploads = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setProgress({ name: file.name, percent: 0, index: i + 1, total: files.length });
          uploads.push(await uploadDirect(file, (percent) =>
            setProgress({ name: file.name, percent, index: i + 1, total: files.length })));
        }
        setProgress(null);
        await axios.post(`/api/forms/request/${token}/update`, { message, uploads, _website: honeypot });
      } else {
        const payload = new FormData();
        payload.append('message', message);
        payload.append('_website', honeypot);
        files.forEach(file => payload.append('files', file));
        await axios.post(`/api/forms/request/${token}/update`, payload);
      }
      setMessage('');
      setFiles([]);
      if (fileInput.current) fileInput.current.value = '';
      setSent(true);
      await load().catch(() => {});
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Something went wrong. Try again in a moment.');
    } finally {
      setProgress(null);
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-blue" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100">Link not active</h1>
          <p className="mt-2 text-gray-500">{notFound} Check the link you were sent.</p>
        </div>
      </div>
    );
  }

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <RequestBrief request={request} fileUrl={fileUrl} zipUrl={zipUrl} />

        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 p-8 sm:p-10">
          <h2 className="text-xs font-black uppercase tracking-widest text-gray-400">Comment or add files</h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Changes, answers to questions, new artwork. It goes straight to the marketing team.
          </p>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-2">Add a comment</label>
              <textarea
                rows={5}
                className={inputClass}
                placeholder="Changes, answers to questions, anything we should know."
                value={message}
                onChange={e => { setMessage(e.target.value); setSent(false); }}
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-2">Add files</label>
              <input
                ref={fileInput}
                type="file"
                multiple
                onChange={e => { setFiles(Array.from(e.target.files).slice(0, request.maxFiles)); setSent(false); }}
                className="block w-full text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-primary-blue file:text-white hover:file:opacity-90"
              />
              {!!files.length && (
                <ul className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  {files.map(f => <li key={f.name}>{f.name} ({formatSize(f.size)})</li>)}
                </ul>
              )}
              <p className="mt-1 text-xs text-gray-400">
                Up to {request.maxFiles} files at a time, {formatSize(request.maxFileBytes)} each.
              </p>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              className="absolute left-[-9999px] w-px h-px opacity-0"
              aria-hidden="true"
            />

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-200">
                {error}
              </div>
            )}
            {sent && !error && (
              <div className="rounded-xl bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-4 py-3 text-sm font-bold text-green-800 dark:text-green-200">
                Sent. Your update was added to the request and the team has been notified.
              </div>
            )}

            {progress && (
              <div>
                <div className="flex justify-between text-xs font-bold text-gray-600 dark:text-gray-300 mb-1">
                  <span className="truncate pr-3">Uploading {progress.name}</span>
                  <span>{progress.index} of {progress.total} · {progress.percent}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div className="h-full bg-primary-blue transition-[width] duration-200" style={{ width: `${progress.percent}%` }} />
                </div>
                <p className="mt-1 text-xs text-gray-400">Large files take a few minutes. Leave this page open.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={sending}
              className="w-full py-3.5 rounded-xl bg-primary-blue text-white font-black uppercase tracking-widest text-sm hover:opacity-90 disabled:opacity-50 transition"
            >
              {sending ? (progress ? 'Uploading…' : 'Sending…') : 'Send update'}
            </button>
          </form>
        </div>

        {!!request.updates.length && (
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 p-8 sm:p-10">
            <h2 className="text-sm font-black uppercase tracking-widest text-gray-600 dark:text-gray-300">Your updates</h2>
            <ul className="mt-4 space-y-4">
              {[...request.updates].reverse().map((u, i) => (
                <li key={i} className="border-l-4 border-primary-blue/40 pl-4">
                  <p className="text-xs text-gray-400">{formatWhen(u.createdAt)}</p>
                  {u.message && <p className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">{u.message}</p>}
                  {!!u.files?.length && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Files: {u.files.join(', ')}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestUpdate;
